import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { generatePath, useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ExternalLink, FileText } from "lucide-react";
import { toaster } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import TextArea from "@/components/ui/textarea";
import { formFileDownloadByPathUrl, handleMutationError } from "@/lib/utils";
import { getExaminerAssignment, submitScoreSheet } from "./examiner.api";
import { api } from "@/lib/api";
import {
	submitScoreSheetSchema,
	type ExaminerAssignmentResponse,
	type SubmitScoreSheetData,
	type SubmitScoreSheetFormValues
} from "./examiner.schema";
import { routes } from "@/app/routes/paths";

const defaultScoreFields: Array<{
	name: keyof Omit<SubmitScoreSheetFormValues, "general_comments">;
	label: string;
	description: string;
	max: number;
}> = [
		{
			name: "literature_review_score",
			label: "Literature Review",
			description: "Assess the quality and relevance of the literature review.",
			max: 15,
		},
		{
			name: "design_method_score",
			label: "Design / Method",
			description: "Assess the appropriateness of the design and methodology.",
			max: 20,
		},
		{
			name: "presentation_score",
			label: "Presentation",
			description: "Assess the presentation and organization of the work.",
			max: 20,
		},
		{
			name: "discussion_score",
			label: "Discussion",
			description: "Assess the depth and quality of the discussion.",
			max: 30,
		},
		{
			name: "clarity_score",
			label: "Clarity",
			description: "Assess clarity, coherence, and justification of conclusions.",
			max: 10,
		},
		{
			name: "comments_score",
			label: "Comments Score",
			description: "Assess the overall quality reflected in your comments.",
			max: 5,
		},
	];

export const ExaminerScoreSheet = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { examinerId, dissertationId } = useParams<{ examinerId: string; dissertationId: string }>();
	const [filesExpanded, setFilesExpanded] = useState(false);

	const form = useForm<SubmitScoreSheetFormValues, unknown, SubmitScoreSheetData>({
		resolver: zodResolver(submitScoreSheetSchema),
		defaultValues: {
			literature_review_score: undefined,
			design_method_score: undefined,
			presentation_score: undefined,
			discussion_score: undefined,
			clarity_score: undefined,
			comments_score: undefined,
			general_comments: "",
		},
	});

	const assignmentQuery = useQuery<ExaminerAssignmentResponse>({
		queryKey: ["examiner-assignment", dissertationId, examinerId],
		queryFn: async () => {
			if (!examinerId || !dissertationId) {
				throw new Error("Missing examiner or dissertation information in the page URL.");
			}

			return getExaminerAssignment(dissertationId, examinerId);
		},
		enabled: Boolean(examinerId && dissertationId),
		retry: false,
		refetchOnWindowFocus: false,
	});

	const assignment = assignmentQuery.data?.data;
	const dissertation = assignment?.dissertation ?? null;

	const { data: filesResp, isLoading: isFilesLoading } = useQuery({
		queryKey: ["examiner-score-sheet-files", assignment?.dissertation?.key],
		queryFn: async () => {
			const res = await api.get(`/review-center/${dissertation?.key}/${assignment?.assignment?.key}/files`);
			return res.data;
		},
		enabled: Boolean(assignment?.dissertation?.key),
	});

	const files: { name: string; path: string }[] = Object.values(filesResp?.data ?? {});
	// const scoreFields = useMemo(() => {
	// 	if (!assignment?.sections?.length) {
	// 		return defaultScoreFields;
	// 	}

	// 	return assignment.sections.map((section) => ({
	// 		name: section.field,
	// 		label: section.section ? `${section.section}. ${section.label}` : section.label,
	// 		description: `Maximum score: ${section.max_score}`,
	// 		max: section.max_score,
	// 	}));
	// }, [assignment?.sections]);

	const watchedScores = form.watch([
		"literature_review_score",
		"design_method_score",
		"presentation_score",
		"discussion_score",
		"clarity_score",
		"comments_score",
	]);

	const totalScore = (watchedScores as Array<number | string | undefined>).reduce<number>(
		(sum, value) => sum + (Number(value) || 0),
		0,
	);

	const mutation = useMutation({
		mutationFn: async (values: SubmitScoreSheetData) => {
			if (!examinerId || !dissertationId) {
				throw new Error("Missing examiner or dissertation information in the page URL.");
			}

			return submitScoreSheet(dissertationId, examinerId, values);
		},
		onSuccess: () => {
			toaster("success", "Score sheet submitted successfully");
			navigate(generatePath(routes.EXAMINER_FEEDBACK_PATH, { examinerId, dissertationId }));
		},
		onError: handleMutationError,
	});

	useEffect(() => {
		document.title = "Examiner Score Sheet - GCPS Resident Portal";
		return () => {
			document.title = "GCPS Resident Portal";
		};
	}, [examinerId, dissertationId]);

	useEffect(() => {
		if (assignment?.score_sheet_submitted) {
			navigate(generatePath(routes.EXAMINER_FEEDBACK_PATH, { examinerId, dissertationId }));
		}

	}, [assignment?.score_sheet_submitted, location.pathname, navigate, examinerId, dissertationId]);

	if (assignmentQuery.isLoading) {
		return (
			<div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-15">
				<div className="mx-auto max-w-4xl rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600 dark:border-gray-700 dark:bg-transparent dark:text-gray-300">
					Loading score sheet...
				</div>
			</div>
		);
	}

	if (assignmentQuery.isError || !assignment) {
		return (
			<div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-15">
				<div className="mx-auto max-w-4xl rounded-lg border border-red-200 bg-white p-6 dark:border-red-800 dark:bg-transparent">
					<h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Score Sheet Unavailable</h1>
					<p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
						We could not load this examiner score sheet at the moment. Please confirm the link is valid and try again later.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-15">
			<div className="mx-auto max-w-7xl space-y-6 px-4">
				<div className="space-y-2">
					<h1 className="text-3xl font-bold text-gray-900 dark:text-white">Candidate Score Sheet</h1>
					<p className="text-gray-600 dark:text-gray-400">
						{assignment.dissertation?.candidate?.name
							? `Enter scores for ${assignment.dissertation.candidate.name} and submit your general comments.`
							: "Enter the candidate's scores for each section and submit your general comments."}
					</p>
				</div>

				<div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start">
					<aside className="space-y-6 lg:sticky lg:top-6">

						<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white">
							<button
								type="button"
								onClick={() => setFilesExpanded((value) => !value)}
								className="flex w-full items-center justify-between bg-stone-50 px-6 py-5 text-left transition-colors hover:bg-stone-100"
							>
								<div className="flex items-center gap-3">
									<div className="rounded-2xl bg-white p-3 shadow-sm">
										<FileText className="h-5 w-5 text-stone-700" />
									</div>
									<div>
										<h2 className="font-semibold">Candidate Files</h2>
										<p className="text-sm text-stone-600">{files.length} file(s) available</p>
									</div>
								</div>
								<ChevronDown className={`h-5 w-5 transition-transform ${filesExpanded ? "rotate-0" : "-rotate-90"}`} />
							</button>

							{filesExpanded && (
								<div className="space-y-3 p-6">
									{isFilesLoading ? (
										<div className="text-sm text-stone-600">Loading files...</div>
									) : files.length ? (
										<>
											{files.map((file) => (
												<div key={file.name} className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
													<div className="truncate font-medium text-stone-900">
														{/* {file.path?.split("/")[file.path?.split("/").length - 1]} */}
														{file.name.replaceAll("_", " ").charAt(0).toUpperCase() + file.name.replaceAll("_", " ").slice(1)}
													</div>
													<div className="mt-1 text-sm text-stone-500">Candidate submission file</div>
													<div className="mt-3 flex flex-wrap gap-2">
														<a href={formFileDownloadByPathUrl(`dissertations/${assignment?.dissertation?.key}/${file.name}`)}>
															<Button variant="outline" size="sm" type="button">
																<ExternalLink className="mr-2 h-4 w-4" />
																View
															</Button>
														</a>
													</div>
												</div>
											))}
										</>
									) : (
										<div className="text-sm text-stone-600">No files found for this dissertation.</div>
									)}
								</div>
							)}
						</section>
						<div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
							<div className="mb-4">
								<h2 className="text-lg font-semibold text-stone-900">Examiner Information</h2>
								<p className="text-sm text-stone-600">
									Details for the examiner attached to this score sheet.
								</p>
							</div>

							<div className="space-y-4">
								<div className="rounded-2xl bg-stone-50 p-4">
									<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Examiner Name</div>
									<div className="mt-2 font-medium text-stone-900">
										{`${assignment.examiner?.full_name}`.trim() || "—"}
									</div>
								</div>

								<div className="rounded-2xl bg-stone-50 p-4">
									<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Email</div>
									<div className="mt-2 text-sm text-stone-900 break-all">
										{assignment.examiner?.email || "—"}
									</div>
								</div>

								<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
									<div className="rounded-2xl bg-stone-50 p-4">
										<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Type</div>
										<div className="mt-2 font-medium capitalize text-stone-900">
											{assignment.examiner?.type || assignment.assignment?.type_name || "—"}
										</div>
									</div>

									<div className="rounded-2xl bg-stone-50 p-4">
										<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Mobile</div>
										<div className="mt-2 font-medium text-stone-900">
											{assignment.examiner?.mobile || "—"}
										</div>
									</div>
								</div>

								<div className="rounded-2xl bg-stone-50 p-4">
									<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Institution</div>
									<div className="mt-2 text-sm leading-6 text-stone-900">
										{assignment.examiner?.institution || "—"}
									</div>
								</div>

								<div className="rounded-2xl bg-stone-50 p-4">
									<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Faculty</div>
									<div className="mt-2 font-medium text-stone-900">
										{assignment.examiner?.faculty?.name || "—"}
									</div>
								</div>
							</div>
						</div>

						<div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
							<div className="mb-4">
								<h2 className="text-lg font-semibold text-stone-900">Candidate Information</h2>
								<p className="text-sm text-stone-600">
									Key details to keep in view while you complete the score sheet.
								</p>
							</div>

							<div className="space-y-4">
								<div className="rounded-2xl bg-stone-50 p-4">
									<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Candidate Name</div>
									<div className="mt-2 font-medium text-stone-900">
										{assignment.dissertation?.candidate?.name || "—"}
									</div>
								</div>

								<div className="rounded-2xl bg-stone-50 p-4">
									<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Dissertation Title</div>
									<div className="mt-2 text-sm leading-6 text-stone-900">
										{assignment.dissertation?.title || "—"}
									</div>
								</div>

								<div className="rounded-2xl bg-stone-50 p-4">
									<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Faculty</div>
									<div className="mt-2 font-medium text-stone-900">
										{assignment.dissertation?.faculty?.name || "—"}
									</div>
								</div>
							</div>
						</div>

					</aside>
					<div className="space-y-6">
						<div className="rounded-2xl bg-blue-50 p-4">
							<div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Running Total</div>
							<div className="mt-2 text-3xl font-bold text-blue-950">{totalScore} / 100</div>
						</div>
						<Form {...form}>
							<form onSubmit={form.handleSubmit((values) => mutation.mutate(values))} className="space-y-6">
								<div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-transparent">
									<div className="grid gap-6 md:grid-cols-2">
										{defaultScoreFields.map((fieldConfig) => (
											<FormField
												key={fieldConfig.name}
												control={form.control}
												name={fieldConfig.name}
												render={({ field }) => {
													const inputValue =
														typeof field.value === "number" || typeof field.value === "string"
															? field.value
															: "";

													return (
														<FormItem>
															<FormLabel>{fieldConfig.label}</FormLabel>
															<p className="text-sm text-muted-foreground">{fieldConfig.description}</p>
															<FormControl>
																<Input
																	type="number"
																	min={0}
																	max={fieldConfig.max}
																	placeholder={`0 - ${fieldConfig.max}`}
																	value={inputValue}
																	onChange={(event) => field.onChange(event.target.value)}
																	disabled={mutation.isPending}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													);
												}}
											/>
										))}
									</div>
								</div>

								<div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-transparent">
									<FormField
										control={form.control}
										name="general_comments"
										render={({ field }) => (
											<FormItem>
												<FormControl>
													<TextArea
														{...field}
														label="General Comments"
														placeholder="Enter your overall feedback for the candidate"
														disabled={mutation.isPending}
														className="min-h-40"
														value={typeof field.value === "string" ? field.value : ""}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="p-3 border rounded-lg bg-blue-50 border-blue-200">
									<p className="font-medium text-sm">Attachments, additional feedbacks / suggestions to residents can be added on the next page after submitting this form</p>
								</div>
								<div className="flex justify-end">
									<Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
										Submit Scores
									</Button>
								</div>
							</form>
						</Form>
					</div>
				</div>
			</div>
		</div >
	);
};
